import { Button, ToolbarProps } from '@progress/kendo-react-buttons';
import {
  DownloadEvent,
  PDFViewer as KendoPDFViewer,
  PDFViewerHandle,
  PDFViewerTool,
  ZoomEvent,
} from '@progress/kendo-react-pdf-viewer';
import { degrees, PDFDocument } from 'pdf-lib';
import { Children, ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { base64ToArrayBuffer, uint8ArrayToBase64 } from 'src/utils/fileTransformation';

interface PDFViewerProps {
  data: string; // base64 string
  fileName: string;
  height?: string;
  url?: string;
}

const tools: PDFViewerTool[] = ['pager', 'zoomInOut', 'zoom', 'download', 'print'];

const PDFViewer = forwardRef(
  (
    { data, fileName, height = '100%', url }: PDFViewerProps,
    ref: ForwardedRef<PDFViewerHandle>
  ) => {
    const [pdfData, setPdfData] = useState(data);
    const [zoom, setZoom] = useState(0.75);
    const [rotateDeg, setRotateDeg] = useState(0);

    useEffect(() => {
      if (data) {
        setPdfData(data);
      }
    }, [data]);

    const handleRotatePDF = useCallback(async () => {
      let degree = rotateDeg;
      if (degree === 270) {
        degree = 0;
      } else {
        degree += 90;
      }
      const PdfArrayBufferBytes = base64ToArrayBuffer(pdfData);
      const pdf = await PDFDocument.load(PdfArrayBufferBytes);
      const pages = pdf.getPages();

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        page.setRotation(degrees(degree));
      }
      const PdfBytes = await pdf.save();
      const url = uint8ArrayToBase64(PdfBytes);
      setPdfData(url);
      setRotateDeg(degree);
    }, [pdfData, rotateDeg]);

    const handleDownLoadPDF = useCallback(
      (event: DownloadEvent) => {
        if (url) {
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return false;
        }
        return true;
      },
      [fileName, url]
    );

    const onRenderToolbar = useCallback(
      (toolbar: React.ReactElement<ToolbarProps>) => {
        const buttons = Children.toArray(toolbar.props.children);
        return (
          <toolbar.type {...toolbar.props} id="pdf-viewer-toolbar">
            {[
              ...buttons,
              <Button key="rotate" className="buttons-container-button" onClick={handleRotatePDF}>
                <span className="k-icon k-font-icon k-i-rotate" />
              </Button>,
            ]}
          </toolbar.type>
        );
      },
      [handleRotatePDF]
    );

    const handleZoom = useCallback((event: ZoomEvent) => {
      setZoom(event.zoom);
    }, []);

    return (
      <>
        <KendoPDFViewer
          ref={ref}
          data={pdfData}
          style={{
            height,
          }}
          zoom={zoom}
          tools={tools}
          saveFileName={fileName}
          onRenderToolbar={onRenderToolbar}
          onZoom={handleZoom}
          onDownload={handleDownLoadPDF}
        />
      </>
    );
  }
);

export default PDFViewer;
